import { useEffect, useRef, useState } from 'react';
import * as Sentry from '@sentry/react';

import {
  RUN_LIFECYCLE_EVENT_TYPES,
  streamAgentRunEvents,
  type AgentWorkflowEvent,
} from '@/services/agents/run-events-stream';
import { pauseReviewContent, savedWorkflowId } from './paused-node';
import {
  type AgentChatRunOutcome,
  type AgentChatRunSnapshot,
  isTerminalRunStatus,
  loadStoredRunState,
  progressOf,
  RUN_FAILED_FALLBACK,
  terminalEventLabel,
  timestampMs,
} from './stored-run-state';

export type {
  AgentChatRunSnapshot,
  AgentChatRunStatus,
} from './stored-run-state';

const MAX_RECONNECTS = 5;
const RECONNECT_DELAY_MS = 2_000;
const RUN_CACHE_TTL_MS = 30 * 60 * 1_000;
const MAX_CACHED_RUNS = 100;

interface CachedRunState {
  snapshot: AgentChatRunSnapshot;
  lastSequence: number;
  completedNodeIds: string[];
  totalNodes: number;
  cachedAt: number;
}

const runStateCache = new Map<string, CachedRunState>();

function cacheKey(
  orgId: string | undefined,
  agentId: string | undefined,
  runId: string,
): string | null {
  return orgId && agentId && runId ? `${orgId}:${agentId}:${runId}` : null;
}

function readCachedRun(key: string | null): CachedRunState | null {
  if (!key) return null;
  const cached = runStateCache.get(key);
  if (!cached) return null;
  if (Date.now() - cached.cachedAt > RUN_CACHE_TTL_MS) {
    runStateCache.delete(key);
    return null;
  }
  return cached;
}

function writeCachedRun(key: string, state: CachedRunState) {
  const current = runStateCache.get(key);
  if (current && current.lastSequence > state.lastSequence) return;
  if (
    current &&
    current.lastSequence === state.lastSequence &&
    isTerminalRunStatus(current.snapshot.status) &&
    !isTerminalRunStatus(state.snapshot.status)
  ) {
    return;
  }
  runStateCache.delete(key);
  runStateCache.set(key, state);
  while (runStateCache.size > MAX_CACHED_RUNS) {
    const oldest = runStateCache.keys().next().value;
    if (typeof oldest !== 'string') break;
    runStateCache.delete(oldest);
  }
}

export function clearAgentChatRunCache() {
  runStateCache.clear();
}

function textField(data: Record<string, unknown>, key: string): string {
  const value = data[key];
  if (typeof value === 'string' && value) return value;
  const extra = data.extra;
  if (extra && typeof extra === 'object') {
    const nested = (extra as Record<string, unknown>)[key];
    if (typeof nested === 'string') return nested;
  }
  return '';
}

function displayName(event: AgentWorkflowEvent): string {
  return event.node?.display_name || event.node?.node_type || 'Workflow';
}

function terminalStatus(state: string | null): AgentChatRunOutcome | null {
  switch (state) {
    case 'TASK_STATE_COMPLETED':
      return 'completed';
    case 'TASK_STATE_FAILED':
    case 'TASK_STATE_REJECTED':
      return 'failed';
    case 'TASK_STATE_CANCELED':
      return 'canceled';
    default:
      return null;
  }
}

export function useAgentChatRun({
  orgId,
  agentId,
  runId,
  workflowId,
  isHistorical = false,
}: {
  orgId?: string;
  agentId?: string;
  runId: string;
  /**
   * The workflow this run belongs to, when the caller already knows it.
   * With it, a historical run is painted from its stored state first: a
   * finished run shows its outcome at once and never opens the event
   * stream; an active run shows its real progress at once and then
   * follows the stream live. Without it the run is folded from its replay.
   * Read once, when the subscription opens -- a value that turns up later
   * does not restart a stream that is already following the run.
   */
  workflowId?: string | null;
  /**
   * True when this run predates this mount (loaded from conversation
   * history) rather than just having been launched live -- only a
   * historical run may already be well underway, with events about to
   * replay from the start, so only it waits on a2a.backfill_complete
   * before painting anything. A live run has no history to replay: its
   * first event already reflects the true current state.
   */
  isHistorical?: boolean;
}): AgentChatRunSnapshot {
  const key = cacheKey(orgId, agentId, runId);
  const [snapshot, setSnapshot] = useState<AgentChatRunSnapshot>(() => {
    const cached = readCachedRun(key);
    return (
      cached?.snapshot ?? {
        status: 'running',
        isHydrating: isHistorical && !!key,
        currentEvent: 'Starting workflow…',
        progress: 0,
      }
    );
  });
  const lastSequenceRef = useRef(0);
  const completedNodesRef = useRef(new Set<string>());
  const totalNodesRef = useRef(0);
  const workflowIdRef = useRef(workflowId);
  workflowIdRef.current = workflowId;

  useEffect(() => {
    if (!orgId || !agentId || !runId || !key) return;

    const controller = new AbortController();
    const cached = readCachedRun(key);
    let terminal = !!cached && isTerminalRunStatus(cached.snapshot.status);
    let failures = 0;
    let replaying = isHistorical && !cached;
    let replaySnapshot: AgentChatRunSnapshot =
      cached?.snapshot ??
      ({
        status: 'running',
        isHydrating: isHistorical,
        currentEvent: 'Starting workflow…',
        progress: 0,
      } satisfies AgentChatRunSnapshot);
    let visibleSnapshot = replaySnapshot;
    lastSequenceRef.current = cached?.lastSequence ?? 0;
    completedNodesRef.current = new Set(cached?.completedNodeIds ?? []);
    totalNodesRef.current = cached?.totalNodes ?? 0;
    setSnapshot(replaySnapshot);

    const cacheVisibleSnapshot = () => {
      writeCachedRun(key, {
        snapshot: visibleSnapshot,
        lastSequence: lastSequenceRef.current,
        completedNodeIds: [...completedNodesRef.current],
        totalNodes: totalNodesRef.current,
        cachedAt: Date.now(),
      });
    };

    const updateSnapshot = (
      update: (current: AgentChatRunSnapshot) => AgentChatRunSnapshot,
    ) => {
      if (replaying) {
        replaySnapshot = update(replaySnapshot);
        return;
      }
      visibleSnapshot = update(visibleSnapshot);
      cacheVisibleSnapshot();
      setSnapshot(visibleSnapshot);
    };

    /**
     * Ends replay and reveals the final snapshot -- the one and only exit
     * from the buffered state, so every terminal path (backfill complete,
     * stream end, exhausted retries) merges its patch through here rather
     * than open-coding `replaying ? replaySnapshot : current` at each site.
     *
     * Defaults `connectionError` to cleared: reaching any settle path at all
     * (an event, a stream end, even a giving-up-on-retries error) means the
     * stream is either connected right now or is done, so a `connectionError`
     * left over from an earlier reconnect no longer describes reality.
     * `onError`'s own settle call overrides it back on, deliberately, by
     * returning it from its patch.
     */
    const settleSnapshot = (
      patch: (base: AgentChatRunSnapshot) => Partial<AgentChatRunSnapshot>,
    ) => {
      if (replaying) {
        replaying = false;
        const base = replaySnapshot;
        visibleSnapshot = {
          ...base,
          isHydrating: false,
          connectionError: undefined,
          ...patch(base),
        };
        cacheVisibleSnapshot();
        setSnapshot(visibleSnapshot);
        return;
      }
      visibleSnapshot = {
        ...visibleSnapshot,
        isHydrating: false,
        connectionError: undefined,
        ...patch(visibleSnapshot),
      };
      cacheVisibleSnapshot();
      setSnapshot(visibleSnapshot);
    };

    const flushSettled = () => settleSnapshot(() => ({}));

    const updateProgress = () => {
      const total = totalNodesRef.current;
      if (!total) return;
      const progress = progressOf(completedNodesRef.current.size, total);
      updateSnapshot((current) => ({ ...current, progress }));
    };

    let confirmingCompletion = false;
    /**
     * A stopped run reaches the stream looking exactly like a finished one:
     * there is no canceled event type, so tearing a run down emits
     * `workflow.run_completed` and ends the stream in TASK_STATE_COMPLETED --
     * the same two frames a natural finish sends. The stored run row is the
     * only place the difference survives, so a completed outcome folded from
     * the stream is checked against it before the card tells the user the work
     * is done. The stop need not have come from this card, or even this tab,
     * for the card to be the thing reporting it.
     */
    const confirmCompletedOutcome = async () => {
      if (confirmingCompletion) return;
      confirmingCompletion = true;
      const base = replaying ? replaySnapshot : visibleSnapshot;
      // An unsaved canvas has no run detail to ask for. The id off
      // run_started is the one a run the agent started carries.
      const storedFrom = savedWorkflowId(
        base.workflowId ?? workflowIdRef.current ?? undefined,
      );
      if (!storedFrom) return;
      const stored = await loadStoredRunState(storedFrom, runId, orgId);
      if (controller.signal.aborted) return;
      if (stored?.snapshot.status !== 'canceled') return;
      updateSnapshot((current) =>
        current.status === 'completed'
          ? {
              ...current,
              status: 'canceled',
              currentEvent: terminalEventLabel('canceled'),
            }
          : current,
      );
    };

    const handleEvent = (event: AgentWorkflowEvent) => {
      failures = 0;
      if (event.event_type === 'a2a.backfill_complete') {
        flushSettled();
        return;
      }
      if (typeof event.sequence === 'number') {
        lastSequenceRef.current = Math.max(
          lastSequenceRef.current,
          event.sequence,
        );
      }
      updateSnapshot((current) =>
        current.connectionError
          ? { ...current, connectionError: undefined }
          : current,
      );
      const at = timestampMs(event.timestamp);
      const name = displayName(event);

      switch (event.event_type) {
        case 'workflow.run_started': {
          const workflowId =
            typeof event.data.workflow_id === 'string'
              ? event.data.workflow_id
              : undefined;
          const total = Number(event.data.total_sub_agents ?? 0);
          if (Number.isFinite(total) && total > 0)
            totalNodesRef.current = total;
          updateSnapshot((current) => ({
            ...current,
            status: 'running',
            workflowId: workflowId ?? current.workflowId,
            startedAt: at ?? current.startedAt ?? Date.now(),
            currentEvent: 'Preparing workflow…',
          }));
          updateProgress();
          break;
        }
        case 'workflow.run_paused':
          updateSnapshot((current) => ({
            ...current,
            status: 'paused',
            currentEvent: 'Run paused',
            pausedSince: at ?? Date.now(),
          }));
          break;
        case 'workflow.run_resumed': {
          const resumedAt = at ?? Date.now();
          // The pause is over the moment the run moves again -- the node
          // that held it may keep working for a while before it completes.
          updateSnapshot((current) => ({
            ...current,
            status: 'running',
            currentEvent: 'Resuming workflow…',
            pausedNode: undefined,
            pausedTotalMs:
              (current.pausedTotalMs ?? 0) +
              Math.max(0, resumedAt - (current.pausedSince ?? resumedAt)),
            pausedSince: undefined,
          }));
          break;
        }
        case 'workflow.run_completed':
          terminal = true;
          updateSnapshot((current) => ({
            ...current,
            status: 'completed',
            completedAt: at ?? Date.now(),
            currentEvent: 'Run complete',
            progress: 100,
            pausedNode: undefined,
          }));
          void confirmCompletedOutcome();
          break;
        case 'workflow.run_failed':
          terminal = true;
          updateSnapshot((current) => ({
            ...current,
            status: 'failed',
            completedAt: at ?? Date.now(),
            currentEvent: 'Run failed',
            // A run that failed while waiting on someone is waiting no more.
            pausedNode: undefined,
            error:
              textField(event.data, 'error_message') ||
              textField(event.data, 'technical_message') ||
              RUN_FAILED_FALLBACK,
          }));
          break;
        case 'node.node_started':
          updateSnapshot((current) => ({
            ...current,
            currentEvent: `${name}: running`,
          }));
          break;
        case 'node.tool_call': {
          const toolName = textField(event.data, 'tool_name');
          updateSnapshot((current) => ({
            ...current,
            currentEvent: toolName
              ? `${name}: using ${toolName.replace(/_/g, ' ')}`
              : `${name}: working`,
          }));
          break;
        }
        case 'node.input_required': {
          const pausedNodeId = event.node?.node_id;
          const reviewContent = pauseReviewContent(event.data);
          updateSnapshot((current) => ({
            ...current,
            currentEvent: `${name}: waiting for input`,
            pausedNode: pausedNodeId
              ? {
                  nodeId: pausedNodeId,
                  displayName: name,
                  reviewContent,
                  announcedAt: Date.now(),
                }
              : current.pausedNode,
          }));
          break;
        }
        case 'node.node_completed':
        case 'node.node_failed': {
          const nodeId = event.node?.node_id;
          if (nodeId) completedNodesRef.current.add(nodeId);
          updateSnapshot((current) => ({
            ...current,
            currentEvent:
              event.event_type === 'node.node_failed'
                ? `${name}: failed`
                : `${name}: complete`,
            // Only the node that was actually being reviewed ends the
            // pause; another node finishing in parallel does not.
            pausedNode:
              nodeId && current.pausedNode?.nodeId === nodeId
                ? undefined
                : current.pausedNode,
          }));
          updateProgress();
          break;
        }
      }
    };

    const subscribe = async () => {
      while (!controller.signal.aborted && !terminal) {
        let streamFailed = false;
        await streamAgentRunEvents({
          orgId,
          agentId,
          runId,
          sinceSequence: lastSequenceRef.current,
          // The card renders progress, not the chain of thought. Without
          // this filter a reload replays every stored thinking chunk of the
          // run -- thousands of frames for a few minutes of agent work.
          types: RUN_LIFECYCLE_EVENT_TYPES,
          signal: controller.signal,
          onEvent: handleEvent,
          onEnd: (state) => {
            // A run_completed/run_failed/run_canceled event already set
            // terminal (and the real status) via handleEvent -- a stream_end
            // that follows, often with no state of its own, must not
            // override that outcome with a guess.
            const alreadySettled = terminal;
            terminal = true;
            if (alreadySettled) {
              flushSettled();
              return;
            }

            const recognized = terminalStatus(state);
            // The stream is telling us the run is over either way -- an
            // unrecognized state must still leave the terminal state, or the
            // card is stuck showing live controls (and a stale pause/stop)
            // for a run that has already ended.
            const status: AgentChatRunOutcome = recognized ?? 'failed';
            if (!recognized) {
              Sentry.captureException(
                new Error('Unrecognized run terminal state'),
                {
                  extra: {
                    context: 'agent-chat-run-terminal-state',
                    orgId,
                    agentId,
                    runId,
                    state,
                  },
                },
              );
            }
            settleSnapshot((base) => ({
              status,
              completedAt: base.completedAt ?? Date.now(),
              currentEvent: terminalEventLabel(status),
              progress: status === 'completed' ? 100 : base.progress,
              // A run that has ended is waiting on nobody, however it ended
              // -- a pause left standing here would strand the chat in a
              // review that can never resume.
              pausedNode: undefined,
              ...(!recognized && {
                error:
                  base.error ||
                  'The run ended unexpectedly. Check the workflow for details.',
              }),
            }));
            if (status === 'completed') void confirmCompletedOutcome();
          },
          onError: (message) => {
            if (controller.signal.aborted) return;
            // A terminal event (run_completed/failed/canceled) already
            // arrived while buffered -- the subscribe loop is about to break
            // on `terminal` and never retry, so this is the last chance to
            // reveal that outcome. Nothing to reconnect to: waiting on
            // backfill_complete now would leave the card on its hydrating
            // skeleton forever.
            if (terminal) {
              flushSettled();
              return;
            }
            streamFailed = true;
            failures += 1;
            const exhausted = failures >= MAX_RECONNECTS;
            // A historical run's replay isn't over just because a reconnect
            // is needed -- keep the buffer intact through the retry so the
            // eventual backfill-complete (or exhausted-retries bailout,
            // below) is still the only thing that reveals anything. Flushing
            // here instead would paint a half-replayed snapshot as final,
            // then update it event-by-event as the replay actually finishes.
            if (replaying && !exhausted) {
              replaySnapshot = {
                ...replaySnapshot,
                connectionError: 'Reconnecting to live progress…',
              };
              return;
            }
            settleSnapshot(() => ({
              connectionError: exhausted
                ? message
                : 'Reconnecting to live progress…',
            }));
          },
        });

        if (controller.signal.aborted || terminal) break;
        if (!streamFailed || failures >= MAX_RECONNECTS) break;
        await new Promise((resolve) =>
          window.setTimeout(resolve, RECONNECT_DELAY_MS),
        );
      }
    };

    // An unsaved canvas has no run detail the backend could serve.
    const storedWorkflowId = savedWorkflowId(
      workflowIdRef.current ?? undefined,
    );

    const start = async () => {
      if (cached) {
        if (!terminal) {
          await subscribe();
        } else if (cached.snapshot.status === 'completed') {
          // The cache is written as soon as `workflow.run_completed` folds
          // in, before confirmCompletedOutcome()'s stored-row lookup
          // resolves -- an unmount mid-lookup aborts that call and leaves
          // the cache pinned at `completed`, even for a run the stored row
          // says was actually stopped. Recheck rather than trust a cached
          // `completed` snapshot outright; `canceled`/`failed` need no such
          // recheck, since nothing disguises itself as those.
          void confirmCompletedOutcome();
        }
        return;
      }
      if (isHistorical && storedWorkflowId) {
        const stored = await loadStoredRunState(storedWorkflowId, runId, orgId);
        if (controller.signal.aborted) return;
        if (stored) {
          totalNodesRef.current = stored.totalNodes;
          completedNodesRef.current = new Set(stored.completedNodeIds);
          if (stored.terminal) {
            // Over already: show how it ended. There is nothing to follow,
            // so no stream is opened and nothing is replayed.
            terminal = true;
            replaying = false;
            visibleSnapshot = stored.snapshot;
            cacheVisibleSnapshot();
            setSnapshot(visibleSnapshot);
            return;
          }
          // Still going: paint where it stands now. The replay that opens
          // below stays buffered, seeded from this state, so the card does
          // not flicker through the run's history before the live tail.
          replaySnapshot = stored.snapshot;
          setSnapshot(stored.snapshot);
        }
      }
      await subscribe();
    };

    void start();
    return () => controller.abort();
  }, [orgId, agentId, runId, isHistorical, key]);

  return snapshot;
}
